import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { CustomReportServiceBase } from './custom-report/custom-report-base.service';
import {
    BoxResultType,
    CustomReportServiceView,
    GetDataFilterValuesParameters,
    GetServiceTermsColumnChartResultType,
    GetServiceTermsLineChartResultType,
    GetServiceTermsMetricsResultTypeGetServiceTermsTotalizedValuesDetailsAndTotalizedResultDto,
    GetServiceTermsValuesResultType,
    ReportResultColumnDto,
    ServicesTermsClient,
    SharedReportDto
} from './reporting-api-proxies';

@Injectable({
    providedIn: 'root'
})
export class ServiceTermsService extends CustomReportServiceBase<ServicesTermsClient, CustomReportServiceView> {
    constructor(protected override client: ServicesTermsClient) {
        super(client);
    }

    async getValues(parameters: GetDataFilterValuesParameters): Promise<GetServiceTermsValuesResultType[]> {
        return await lastValueFrom(this.client.servicesTermsGetServiceTermsValues(parameters));
    }

    async getBoxesValues(parameters: GetDataFilterValuesParameters): Promise<BoxResultType[]> {
        return await lastValueFrom(this.client.servicesTermsGetServiceTermsBoxes(parameters));
    }

    async getColumnChartValues(
        parameters: GetDataFilterValuesParameters
    ): Promise<GetServiceTermsColumnChartResultType[]> {
        return await lastValueFrom(this.client.servicesTermsGetServiceTermsColumnChartValues(parameters));
    }

    async getMetricsValues(parameters: GetDataFilterValuesParameters): Promise<Record<string, number>> {
        const data = await lastValueFrom(this.client.servicesTermsGetServiceTermsMetrics(parameters));
        return this.transformMetricsData(data);
    }

    async getLineChartValues(parameters: GetDataFilterValuesParameters): Promise<GetServiceTermsLineChartResultType[]> {
        return await lastValueFrom(this.client.servicesTermsGetServiceTermsLineChartValues(parameters));
    }

    override async saveCustomReport(body: CustomReportServiceView): Promise<CustomReportServiceView> {
        if (body.isPublic) return await lastValueFrom(this.client.servicesTermsSaveCatalogReport(body));
        return await lastValueFrom(this.client.servicesTermsSaveCustomReport(body));
    }

    override async getCustomReport(id: number): Promise<CustomReportServiceView> {
        return await lastValueFrom(this.client.servicesTermsGetCustomReport(id));
    }

    override async shareReport(body: CustomReportServiceView): Promise<SharedReportDto> {
        return await lastValueFrom(this.client.servicesTermsCreateSharedReport(body));
    }

    override async getSharedReport(id: string): Promise<CustomReportServiceView> {
        return await lastValueFrom(this.client.servicesTermsGetSharedReport(id));
    }

    override async getResultAvailableColumns(organizationIds: number[]): Promise<ReportResultColumnDto[]> {
        return await lastValueFrom(this.client.servicesTermsResultAvailableColumns(organizationIds));
    }

    transformMetricsData(
        data: GetServiceTermsMetricsResultTypeGetServiceTermsTotalizedValuesDetailsAndTotalizedResultDto
    ): Record<string, number> {
        const ROWS = [
            'quantity',
            'directCost',
            'contingency',
            'escalation',
            'grossMargin',
            'sellPrice',
            'totalDirectCost',
            'totalDirectCostPercent',
            'avgSellPrice',
            'avgSellPricePercentage'
        ]; // "termNumber",

        if (!data.results) return {};

        const transformedData = data.results?.reduce(
            (acc: Record<string, number>, curr) => {
                Object.entries(curr).forEach(([key, value]) => {
                    if (ROWS.includes(key)) {
                        if (key !== 'termNumber') {
                            acc[`term${curr.termNumber}${key}`] = value;
                        }
                    }
                });

                return acc;
            },
            {} as Record<string, number>
        );

        return transformedData;
    }
}
